import { useState, useRef, useEffect } from 'react'
import { Bell, Sparkles, X, ChevronRight, Check } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { useApp } from '../context/AppContext'
import { ModalShell, ModalHeader } from './server/ServerModals'
import './NotificationsBell.css'

interface Notification {
  created_at: string;
  version: string;
  description: string;
}

export function NotificationsBell() {
  const { state } = useApp()
  const [isOpen, setIsOpen] = useState(false)
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const hasNotifications = state.notifications.length > 0

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const stripMarkdown = (text: string) => {
    return text.replace(/[#*`_\[\]]/g, '');
  }

  const truncate = (text: string, length: number) => {
    const plainText = stripMarkdown(text);
    if (plainText.length <= length) return plainText;
    return plainText.substring(0, length) + '...';
  }

  const handleOpenDropdown = () => {
    setIsOpen(!isOpen);
  }

  const openNotificationModal = (notification: Notification) => {
    setSelectedNotification(notification);
    setIsOpen(false);
  }

  return (
    <div className="notifications-container" ref={dropdownRef}>
      <button
        className={`notifications-bell-btn ${isOpen ? 'active' : ''}`}
        onClick={handleOpenDropdown}
        title="Notificaciones"
        aria-label="Notificaciones"
      >
        <Bell size={20} />
        {hasNotifications && <span className="notification-dot"></span>}
      </button>

      {isOpen && (
        <div className="notifications-dropdown">
          <div className="dropdown-header">
            <h3>Novedades <Sparkles size={16} className="sparkle-icon" /></h3>
          </div>
          <div className="dropdown-content">
            {hasNotifications ? (
              state.notifications.map((notification, index) => (
                <div
                  key={index}
                  className="dropdown-item preview"
                  onClick={() => openNotificationModal(notification)}
                >
                  <div className="item-header">
                    <span className="item-title-preview">
                      {stripMarkdown(notification.description.split(':')[0])}
                    </span>
                    <span className="item-version-small">v{notification.version}</span>
                  </div>
                  <div className="item-body">
                    <p className="item-description-preview">
                      {truncate(notification.description.split(':').slice(1).join(':').trim() || notification.description, 40)}
                    </p>
                    <ChevronRight size={14} className="arrow-icon" />
                  </div>
                </div>
              ))
            ) : (
              <div className="dropdown-empty">
                <Bell size={32} className="empty-icon" />
                <p>No hay novedades por ahora</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Full Notification Modal — usa el sistema sp-modal-* */}
      {selectedNotification && (
        <ModalShell onClose={() => setSelectedNotification(null)} size="md">
          <ModalHeader
            icon={<Sparkles size={18} />}
            title="Novedades de la plataforma"
            subtitle={`v${selectedNotification.version} · ${new Date(selectedNotification.created_at).toLocaleDateString()}`}
            onClose={() => setSelectedNotification(null)}
          />
          <div className="sp-modal-body notification-modal-body">
            <div className="notification-full-content markdown-body">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {selectedNotification.description}
              </ReactMarkdown>
            </div>
          </div>
          <div className="sp-modal-footer">
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setSelectedNotification(null)}
            >
              <Check size={14} /> Entendido
            </button>
          </div>
        </ModalShell>
      )}
    </div>
  )
}
